import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { getSuggestionAnnotationsState } from 'modules/common/viewer/pdfViewer/data/reducer';

import {
  createAnnotation,
  type SuggestionAnnotationClassType,
} from './SuggestionAnnotation';
import type { ISuggestionAnnotation } from './SuggestionAnnotation';
import {
  SUGGESTION_ANNOTATION_COMMAND_ORIGINS,
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  SUGGESTION_ANNOTATION_PREDICATE_TYPES,
} from './consts';
import {
  isAnnotationVisible,
  TextPositionSearcher,
  scrollToAnnotation,
} from './suggestionUtils';
import type {
  ActionTextSnippet,
  SuggestionAnnotationCommand,
  TextPositionResult,
  SuggestionAnnotationStateMap,
  SuggestionAnnotationState,
} from './types';

/**
 * Handle rendering, hiding, and focusing suggestion based annotations in response
 * to the suggestionAnnotationsState
 */
export const useSuggestionAnnotations = ({
  isLoaded,
  annotationManager,
  documentViewer,
  webViewerInstance,
  activeFileUuid,
  SuggestionAnnotationClass,
}: {
  isLoaded: boolean;
  annotationManager: Core.AnnotationManager | null;
  documentViewer: Core.DocumentViewer | null;
  webViewerInstance: WebViewerInstance | null;
  activeFileUuid: string | null;
  SuggestionAnnotationClass: SuggestionAnnotationClassType | null;
}) => {
  const suggestionAnnotationsState = useSelector(getSuggestionAnnotationsState);
  const { commands: transientCommands } = suggestionAnnotationsState;

  const dispatch = useDispatch();
  const doc = documentViewer?.getDocument();

  useEffect(() => {
    if (
      isLoaded &&
      doc &&
      activeFileUuid &&
      annotationManager &&
      documentViewer &&
      webViewerInstance &&
      SuggestionAnnotationClass &&
      transientCommands.length > 0
    ) {
      const start = performance.now();
      // first filter down applicable set of annotations to those that are tied
      // to the active file and are of type annotation
      const allSuggestionAnnotations = annotationManager
        .getAnnotationsList()
        .filter(
          (
            annot: Core.Annotations.Annotation,
          ): annot is ISuggestionAnnotation => {
            return (
              annot instanceof SuggestionAnnotationClass &&
              annot.partFileUuid === activeFileUuid
            );
          },
        );

      const currentState: SuggestionAnnotationStateMap = {};
      const annotationsById: Record<string, ISuggestionAnnotation> = {};
      for (const annot of allSuggestionAnnotations) {
        annotationsById[annot.Id] = annot;
        currentState[annot.Id] = annot.getAppState();
      }

      /**
       * Create all explicit annotations from the commands, making sure we do not recreate if
       * they already exist.
       */
      const createAllExplicitAnnotations = (
        commands: SuggestionAnnotationCommand[],
      ) => {
        for (const command of commands) {
          const rawAnnots = [];
          if (command.annotations) {
            rawAnnots.push(...command.annotations);
          }
          // create annotations if not already created and add to tracking objects
          for (const rawAnnot of rawAnnots) {
            if (rawAnnot.partFileId !== activeFileUuid) {
              continue;
            } else if (rawAnnot.id in annotationsById) {
              // already tracked, so continue
              continue;
            } else if (rawAnnot.page && (rawAnnot.loc || rawAnnot.geometry)) {
              // create new, and default to hidden visibility as it will be
              // turned on explicitly when executing downstream command
              const newAnnotation = createAnnotation(
                rawAnnot,
                documentViewer,
                SuggestionAnnotationClass,
              );

              // Add to annotation manager
              annotationManager.addAnnotation(newAnnotation);

              // ensure regions are sent to the back
              if (newAnnotation.rawAnnotation.displayType === 'region') {
                annotationManager.bringToBack(newAnnotation);
              }

              currentState[rawAnnot.id] = newAnnotation.getAppState();
              annotationsById[rawAnnot.id] = newAnnotation;
              allSuggestionAnnotations.push(newAnnotation);
            }
          }
        }
      };

      createAllExplicitAnnotations(transientCommands);

      /**
       * Curate the commands to ensure only the latest focus request is kept and
       * that the focus request happens last.
       */
      const curateCommands = (
        rawCommands: SuggestionAnnotationCommand[],
      ): SuggestionAnnotationCommand[] => {
        const out = [];
        let lastFocusCommand: SuggestionAnnotationCommand | null = null;
        let lastUnfocusCommand: SuggestionAnnotationCommand | null = null;
        for (const cmd of rawCommands) {
          // Make sure we're reserving the very last focus and unfocus requests to be at the
          // end of the stack since they have side effects for other annotations that need to be
          // processed last
          if (cmd.type === SUGGESTION_ANNOTATION_COMMAND_TYPES.FOCUS) {
            lastFocusCommand = cmd;
          } else if (cmd.type === SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS) {
            lastUnfocusCommand = cmd;
          } else {
            out.push(cmd);
          }
        }
        if (lastUnfocusCommand) out.push(lastUnfocusCommand);
        if (lastFocusCommand) out.push(lastFocusCommand);
        return out;
      };

      const curatedTransientCommands = curateCommands(transientCommands);

      const affectedAnnotations = new Set<ISuggestionAnnotation>();

      const getCommandAnnotations = (
        command: SuggestionAnnotationCommand,
      ): ISuggestionAnnotation[] => {
        if (command.predicate) {
          const out = [];
          const { predicate } = command;
          if (
            predicate.type ===
            SUGGESTION_ANNOTATION_PREDICATE_TYPES.NOT_IN_NEXT_STATE
          ) {
            const idSet = new Set(predicate.nextStateAnnotationIds);
            for (const annot of allSuggestionAnnotations) {
              if (!idSet.has(annot.Id)) {
                out.push(annot);
                affectedAnnotations.add(annot);
              }
            }
          } else if (
            predicate.type ===
            SUGGESTION_ANNOTATION_PREDICATE_TYPES.CURRENTLY_FOCUSED
          ) {
            for (const annot of allSuggestionAnnotations) {
              if (annot.isFocused()) {
                out.push(annot);
                affectedAnnotations.add(annot);
              }
            }
          }
          return out;
        } else if (command.annotationIds) {
          const out = [];
          for (const annotId of command.annotationIds) {
            if (annotId in annotationsById) {
              out.push(annotationsById[annotId]);
              affectedAnnotations.add(annotationsById[annotId]);
            }
          }
          return out;
        } else if (command.annotations) {
          const out = [];
          for (const rawAnnot of command.annotations) {
            if (rawAnnot.id in annotationsById) {
              out.push(annotationsById[rawAnnot.id]);
              affectedAnnotations.add(annotationsById[rawAnnot.id]);
            }
          }
          return out;
        } else if (command.mergeState) {
          const out = [];
          for (const annotId of Object.keys(command.mergeState ?? {})) {
            if (annotId in annotationsById) {
              out.push(annotationsById[annotId]);
              affectedAnnotations.add(annotationsById[annotId]);
            }
          }
          return out;
        } else {
          return [];
        }
      };

      const executeShowCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.show();
        }
      };

      const executeHideCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.hide();
        }
      };

      const executeFocusCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);

        // if no annotations found to focus, do not actually execute
        if (annotations.length === 0) {
          return;
        }

        const focusedAnnotationIds = new Set(
          annotations.map((annot) => annot.Id),
        );

        // First, dim and unfocus all other annotations
        for (const annot of allSuggestionAnnotations) {
          if (!focusedAnnotationIds.has(annot.Id)) {
            annot.dim();
            annot.unfocus();
            affectedAnnotations.add(annot);
          }
        }

        // Then focus the target annotations
        for (const annot of annotations) {
          annot.focus();
          // Ensure the annotation we're focusing is in front of all other annotations
        }
        if (annotations.length > 0) {
          // if the source of the command is anywhere but the viewer, center the annotations,
          // otherwise, check for visibility and then center it (for cases where the annotation
          // is only partially visible on the screen)
          if (command.origin !== SUGGESTION_ANNOTATION_COMMAND_ORIGINS.VIEWER) {
            scrollToAnnotation(annotations[0], documentViewer);
          } else if (!isAnnotationVisible(annotations[0], documentViewer)) {
            scrollToAnnotation(annotations[0], documentViewer);
          }
        }
      };

      const executeUnfocusCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);

        // if no annotations found to unfocus, do not actually execute
        if (annotations.length === 0) {
          return;
        }

        // First unfocus the target annotations
        for (const annot of annotations) {
          annot.unfocus();
        }

        // then undim all other annotations if there are no remaining focused annotations
        const anyStillFocused = allSuggestionAnnotations.some((annot) =>
          annot.isFocused(),
        );
        if (!anyStillFocused) {
          for (const annot of allSuggestionAnnotations) {
            if (annot.isDimmed()) {
              annot.undim();
              affectedAnnotations.add(annot);
            }
          }
        }
      };

      const executeDimCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.dim();
        }
      };

      const executeUndimCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.undim();
        }
      };

      const executeWhiteoutCommand = (command: SuggestionAnnotationCommand) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.whiteout();
        }
      };

      const executeUnwhiteoutCommand = (
        command: SuggestionAnnotationCommand,
      ) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          annot.unwhiteout();
        }
      };

      const executeMergeStateCommand = (
        command: SuggestionAnnotationCommand,
      ) => {
        const annotations = getCommandAnnotations(command);
        for (const annot of annotations) {
          const nextState = command.mergeState?.[annot.Id];
          if (nextState) {
            annot.setAppState(nextState);
          }
        }
      };

      const executeCommand = (command: SuggestionAnnotationCommand) => {
        switch (command.type) {
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.SHOW:
            executeShowCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.HIDE:
            executeHideCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.FOCUS:
            executeFocusCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS:
            executeUnfocusCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.DIM:
            executeDimCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.UNDIM:
            executeUndimCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.WHITEOUT:
            executeWhiteoutCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.UNWHITEOUT:
            executeUnwhiteoutCommand(command);
            break;
          case SUGGESTION_ANNOTATION_COMMAND_TYPES.MERGE_STATE_MULTIPLE:
            executeMergeStateCommand(command);
            break;
          default:
            break;
        }
      };

      const allCommands = [...curatedTransientCommands];
      for (const command of allCommands) {
        executeCommand(command);
      }

      // Process action text snippets and send them back through the command queue
      const searchPatterns = transientCommands
        .filter(
          (cmd) =>
            cmd.type === SUGGESTION_ANNOTATION_COMMAND_TYPES.PROCESS_ACTIONS,
        )
        .reduce<ActionTextSnippet[]>(
          (acc, cmd) => acc.concat(cmd?.actionTextSnippets ?? []),
          [],
        );

      if (searchPatterns.length > 0) {
        const callback = (result: TextPositionResult) => {
          dispatch(
            suggestionAnnotations.queueCommands({
              commands: [
                {
                  type: SUGGESTION_ANNOTATION_COMMAND_TYPES.RECEIVE_TEXT_EXTRACTIONS,
                  textExtractions: [result],
                },
              ],
            }),
          );
        };
        const textSearcher = new TextPositionSearcher(
          webViewerInstance,
          callback,
        );
        textSearcher.findTextPositionsOnPage(searchPatterns);
      }
      // build next state map and apply drawing if anything different
      const nextStateMap: SuggestionAnnotationStateMap = {};
      let countRedraws = 0;
      for (const annot of allSuggestionAnnotations) {
        const nextStateObj = annot.getAppState();
        const currentStateObj = currentState[annot.Id] ?? {};

        for (const key of Object.keys(
          nextStateObj,
        ) as (keyof SuggestionAnnotationState)[]) {
          if (nextStateObj[key] !== currentStateObj[key]) {
            // if different, that means we need to redraw
            annotationManager.redrawAnnotation(annot);
            countRedraws++;
            break;
          }
        }

        nextStateMap[annot.Id] = nextStateObj;
      }

      const end = performance.now();

      dispatch(
        suggestionAnnotations.acknowledgeCommands({
          commandIds: transientCommands.map((cmd) => cmd.id),
          nextStateMap,
          countRedraws,
          duration: end - start,
        }),
      );
    }
  }, [
    doc,
    transientCommands,
    isLoaded,
    annotationManager,
    documentViewer,
    activeFileUuid,
    dispatch,
    webViewerInstance,
    SuggestionAnnotationClass,
  ]);
};
